import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useGetSettings } from '../../services/settings.service'
import { getMainMenuItems, isItemActive } from './main-menu.util'

const LOGO_SRC = '/logos/logo-menu.png'
const LOGO_ALT = 'OTEA: Observatorio para la Transición Energética y la Acción Climática'

export const MainMenu: React.FC = () => {
  const currentPage = useLocation().pathname
  const settingsRequest = useGetSettings()
  const showAtlas = settingsRequest.data?.atlas_enabled !== false
  const items = getMainMenuItems(showAtlas)

  const itemClass = (item: ReturnType<typeof getMainMenuItems>[number]) =>
    `item${isItemActive(item, currentPage) ? ' active' : ''}`

  return (
    <TopMenu>
      {/* Franja superior: solo el logo (gris, como antes) */}
      <header className="logoBand">
        <Link to="/">
          <img src={LOGO_SRC} alt={LOGO_ALT} width="100px" height="40px" />
        </Link>
      </header>

      {/* Franja inferior: el menú, con el azul de la marca */}
      <div className="menuBand">
        <nav className="mainNav" aria-label="Menú principal">
          <ul>
            {items.map(item => (
              <li key={item.key}>
                <Link to={item.to} id={item.id} className={itemClass(item)}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Menú móvil (se despliega con la hamburguesa) */}
      <nav className="mobileNav" aria-label="Menú móvil">
        <input type="checkbox" id="menuToggle" />
        <label htmlFor="menuToggle">
          <span>&times;</span>
          <span>&#9776;</span>
        </label>
        <ul className="mobileMenu">
          {items.map(item => (
            <li key={item.key}>
              <Link to={item.to} className={itemClass(item)}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </TopMenu>
  )
}

/** Cada franja de la cabecera mide lo mismo, para que sumen una altura previsible. */
const BAND = '50px'
const BRAND_BLUE = '#2fcdde'
const BAND_GREY = '#efefef'
const INK = '#222'
const INK_STRONG = '#12161c'

/**
 * Mismo margen izquierdo para el logo y para el menú: el del contenedor de
 * contenido (los mismos offsets que usa GeneralContainer), de forma que los dos
 * arranquen en la misma vertical que el texto de las páginas.
 */
const containerInset = css`
  padding-left: 10px;

  @media only screen and (min-width: 1024px) {
    padding-left: calc(4.1667% + 10px);
  }

  @media only screen and (min-width: 1200px) {
    padding-left: calc(8.3333% + 10px);
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(20.8333% + 10px);
  }
`

const TopMenu = styled.div`
  .logoBand {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    height: ${BAND};
    display: flex;
    align-items: center;
    background: ${BAND_GREY};
    ${containerInset}

    a {
      display: block;
      line-height: 0;
    }

    img {
      display: block;
      width: 100px;
      height: 40px;
    }
  }

  .menuBand {
    position: fixed;
    top: ${BAND};
    left: 0;
    right: 0;
    z-index: 1000;
    height: ${BAND};
    background: ${BRAND_BLUE};
    font-size: 0.7em;
    font-weight: bold;
    display: none;

    @media only screen and (min-width: 1024px) {
      display: block;
    }
  }

  .mainNav {
    height: ${BAND};
    ${containerInset}

    ul {
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: center;
      height: ${BAND};
      margin: 0;
      padding: 0;
      list-style: none;
      white-space: nowrap;
    }

    li {
      display: block;
    }

    a {
      display: block;
      height: ${BAND};
      line-height: ${BAND};
      padding: 0 14px;
      color: ${INK};
      text-transform: uppercase;
      transition: background 0.2s linear, color 0.2s linear;

      &:hover {
        background: rgba(255, 255, 255, 0.45);
        color: ${INK_STRONG};
      }

      &.active {
        background: #fff;
        color: ${INK};
      }
    }
  }

  .mobileNav {
    position: fixed;
    top: 0;
    right: 10px;
    min-width: 40px;
    min-height: 40px;
    overflow: hidden;
    z-index: 1100;
    font-size: 1.5em;

    @media only screen and (min-width: 1024px) {
      display: none;
    }

    label {
      display: block;
      width: ${BAND};
      height: ${BAND};
      border-radius: 50%;
      background: #000;
      text-align: center;
      line-height: ${BAND};
      color: #fff;
    }

    .mobileMenu {
      overflow: hidden;
      width: 0;
      height: 0;
      padding: 0;
      margin: 0;
      transition: width 0.5s, height 0.5s;
      list-style: none;

      li {
        a {
          background: rgba(0, 0, 0, 0.8);
          color: #fff;
          height: 35px;
          line-height: 35px;
          border-radius: 20px;
          margin-top: 10px;
          font-size: 16px;
          padding: 0 10px;
          display: block;
        }
      }
    }

    input {
      display: none;

      &:checked ~ label span:last-child {
        display: none;
      }

      &:not(:checked) ~ label span:first-child {
        display: none;
      }

      &:checked ~ ul {
        width: 280px;
        height: 320px;
      }
    }
  }
`
